import {
  ExtractedMedicalData,
  getInitialStages,
  Stages,
  StageUpdate,
} from '@/layers/business-logic/medical-docs/types';
import {
  DocumentParserWebSocketClient,
  WebSocketMessage,
} from '@/layers/business-logic/medical-docs/websocket-client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Constants for localStorage
const STORAGE_KEY_JOB_ID = 'r2h_document_parser_job_id';
const STORAGE_KEY_SOURCE_TYPE = 'r2h_document_parser_source_type';

export type ParserUIState = 'HIDDEN' | 'MINIMIZED' | 'FULL';
export type TabState = 'UPLOAD' | 'REVIEW';

export interface DocumentParserContextState {
  // Job tracking state
  currentJobId: string | null;
  jobStatus: 'PENDING' | 'PROCESSING' | 'COMPLETE' | 'FAILED' | null;
  stages: Stages;
  error: string | null;
  extractedData: ExtractedMedicalData | null;
  uploadedFile: File | null;

  // UI state
  uiState: ParserUIState;
  isProcessing: boolean;
  activeTab: TabState;
  sourceRoute: string | null;
  sourceType: string | null;
  sourceText: string | null;

  // Actions
  initiateProcessing: (
    file: File | null,
    text: string | null,
    currentAdditionalInfo?: string,
  ) => Promise<string>; // Returns jobId
  minimizeParser: () => void;
  maximizeParser: () => void;
  closeParser: () => void;
  retryProcessing: () => Promise<boolean>;
  setActiveTab: (tab: TabState) => void;
  setExtractedData: (data: ExtractedMedicalData | null) => void;
  clearJob: () => void; // New action to explicitly clear job data
  setUploadedFile: (file: File | null) => void;
  setSourceType: (type: string) => void;
  setSourceText: (text: string) => void;
}

const DocumentParserContext = createContext<DocumentParserContextState | null>(null);

interface DocumentParserProviderProps {
  children: React.ReactNode;
}

export const DocumentParserProvider: React.FC<DocumentParserProviderProps> = ({ children }) => {
  // Job state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [jobStatus, setJobStatus] = useState<DocumentParserContextState['jobStatus']>(null);
  // Both start from the default so the server render and the first client render agree.
  // This provider is mounted by the patient and provider layouts, which are server-
  // rendered, and reading localStorage in these initializers would make the browser's
  // first render disagree with the served HTML. The mount effect below restores the
  // stored source type before any recovery logic runs, so nothing is lost by deferring.
  const [sourceType, setSourceType] = useState<string>('text');
  const [stages, setStages] = useState<Stages>(() => getInitialStages('text'));
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedMedicalData | null>(null);
  const [sourceRoute, setSourceRoute] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(false);

  // UI state
  const [uiState, setUiState] = useState<ParserUIState>('HIDDEN');
  const [activeTab, setActiveTab] = useState<TabState>('UPLOAD');
  const [sourceText, setSourceText] = useState<string | null>(null);

  // WebSocket client reference
  const wsClientRef = useRef<DocumentParserWebSocketClient | null>(null);

  // Load job and source type from localStorage on mount BEFORE any recovery logic
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedSourceType = localStorage.getItem(STORAGE_KEY_SOURCE_TYPE) || 'text';
      if (savedSourceType !== sourceType) {
        setSourceType(savedSourceType);
        setStages(getInitialStages(savedSourceType));
      }

      const savedJobId = localStorage.getItem(STORAGE_KEY_JOB_ID);
      if (savedJobId) {
        setCurrentJobId(savedJobId);
        setUiState('MINIMIZED');
      }

      setHydrated(true);
    }
  }, []);

  // Update localStorage when job ID changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (currentJobId) {
        localStorage.setItem(STORAGE_KEY_JOB_ID, currentJobId);
      } else {
        localStorage.removeItem(STORAGE_KEY_JOB_ID);
      }
    }
  }, [currentJobId]);

  // Persist sourceType so we can restore stages correctly across reloads
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_SOURCE_TYPE, sourceType);
    }
  }, [sourceType]);

  // Explicitly clear job from localStorage - to be called when user approves the data
  const clearJob = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_JOB_ID);
      // Keep last chosen sourceType for UX, but reset stages using current sourceType
    }
    setExtractedData(null);
    setSourceText(null);
    setCurrentJobId(null);
    setJobStatus(null);
    setStages(getInitialStages(sourceType));
    setError(null);
    setUploadedFile(null);
    setActiveTab('UPLOAD');
  }, [sourceType]);

  // Computed state
  const isProcessing = useMemo(() => {
    return jobStatus === 'PENDING' || jobStatus === 'PROCESSING';
  }, [jobStatus]);

  // Reset WebSocket connection and clean up resources
  const resetConnection = useCallback(() => {
    if (wsClientRef.current) {
      wsClientRef.current.disconnect();
      wsClientRef.current = null;
    }
  }, []);

  const handleWebSocketMessage = useCallback(
    (message: WebSocketMessage) => {
      console.log('Processing WebSocket message:', message);
      if (!message.jobId) {
        return;
      }

      // Helper to get a stable stage name by id (covers both text and upload flows)
      const getStageNameById = (id: string): string => {
        const union = {
          ...getInitialStages('upload'),
          ...getInitialStages('text'),
        } as Stages;
        return union[id]?.name || id;
      };

      switch (message.type) {
        case 'status':
          if (message.data) {
            const stageUpdate = message.data as StageUpdate;

            setStages(prevStages => {
              const newStages = { ...prevStages };
              const stageToUpdateId = stageUpdate.id;

              // Ensure the stage entry exists with a proper name
              if (!newStages[stageToUpdateId]) {
                newStages[stageToUpdateId] = {
                  id: stageToUpdateId,
                  name: getStageNameById(stageToUpdateId),
                  status: 'PENDING',
                } as any;
              }

              newStages[stageToUpdateId] = {
                ...newStages[stageToUpdateId],
                status: stageUpdate.status,
                message: stageUpdate.message,
              };

              return newStages;
            });

            // Handle FAILED status for a stage
            if (stageUpdate.status === 'FAILED') {
              const errorMsg = stageUpdate.message || `Failed at ${stageUpdate.id} stage`;
              setError(errorMsg);
              setJobStatus('FAILED');
              resetConnection();
            }
            // If this is the final stage and it's complete, close connection and switch to review
            else if (stageUpdate.id === 'RESULT' && stageUpdate.status === 'COMPLETE') {
              resetConnection();
              setExtractedData(stageUpdate.data);
              setJobStatus('COMPLETE');
              setActiveTab('REVIEW');
            }
          }
          break;

        case 'error':
          // Processing error
          if (message.data && 'message' in message.data) {
            const errorMsg = (message.data as { message: string }).message;
            console.log('Setting error:', errorMsg);
            setError(errorMsg);
            setJobStatus('FAILED');
            resetConnection();
          }
          break;

        default:
          console.log('Unhandled message type:', message.type);
          break;
      }
    },
    [resetConnection],
  );

  // Initialize WebSocket connection
  const connectToWebSocket = useCallback(
    (jobId: string) => {
      // Close existing connection if any
      resetConnection();

      // Get WebSocket endpoint from environment variables
      const wsEndpoint = process.env.NEXT_PUBLIC_WEBSOCKET_API_ENDPOINT;
      if (!wsEndpoint) {
        console.error('WebSocket endpoint not configured');
        setError('WebSocket communication not configured');
        return;
      }

      // Create new WebSocket client with a fixed userId (or get from context if available)
      const userId = 'frontend-user'; // Generate a unique userId

      wsClientRef.current = new DocumentParserWebSocketClient(wsEndpoint, jobId, userId, {
        onOpen: () => {
          console.log('WebSocket connection opened');
        },
        onMessage: (message: WebSocketMessage) => {
          handleWebSocketMessage(message);
        },
        onError: event => {
          console.error('WebSocket error', event);
          // Check if we should show error to user
          if (jobStatus === 'PROCESSING') {
            setError('Connection error. Trying to reconnect...');
          }
        },
        onClose: event => {
          console.log('WebSocket connection closed', event);
          // If abnormal closure and we're still processing
          if (event.code !== 1000 && jobStatus === 'PROCESSING') {
            // We'll try to reconnect automatically via the client
            // Possibly show a UI indication
          }
        },
        autoReconnect: true,
        maxReconnectAttempts: 5,
      });

      // Connect to WebSocket
      wsClientRef.current.connect();
    },
    [jobStatus, handleWebSocketMessage, resetConnection],
  );

  // Initialize WebSocket connection with explicit endpoint
  const connectToWebSocketWithEndpoint = useCallback(
    (jobId: string, wsEndpoint: string) => {
      // Close existing connection if any
      resetConnection();

      // Create new WebSocket client with a fixed userId (or get from context if available)
      const userId = 'frontend-user'; // Generate a unique userId

      wsClientRef.current = new DocumentParserWebSocketClient(wsEndpoint, jobId, userId, {
        onOpen: () => {
          console.log('WebSocket connection opened');
        },
        onMessage: (message: WebSocketMessage) => {
          handleWebSocketMessage(message);
        },
        onError: event => {
          console.error('WebSocket error', event);
          // Check if we should show error to user
          if (jobStatus === 'PROCESSING') {
            setError('Connection error. Trying to reconnect...');
          }
        },
        onClose: event => {
          console.log('WebSocket connection closed', event);
          // If abnormal closure and we're still processing
          if (event.code !== 1000 && jobStatus === 'PROCESSING') {
            // We'll try to reconnect automatically via the client
            // Possibly show a UI indication
          }
        },
        autoReconnect: true,
        maxReconnectAttempts: 5,
      });

      // Connect to WebSocket
      wsClientRef.current.connect();
    },
    [jobStatus, handleWebSocketMessage, resetConnection],
  );

  // Initiate file processing
  const initiateProcessing = useCallback(
    async (
      file: File | null,
      text: string | null,
      currentAdditionalInfo?: string,
    ): Promise<string> => {
      try {
        // Reset state
        const initialStages = getInitialStages(sourceType);
        setStages(initialStages); // Start with all predefined stages

        setExtractedData(null);
        setError(null);
        setJobStatus('PENDING');
        setActiveTab('UPLOAD');

        // Preprocess text if provided
        let processedText = text;
        if (text) {
          // Clean up the text to handle potential encoding issues
          processedText = text
            .trim()
            .replace(/\r\n/g, '\n') // Normalize line endings
            .replace(/\r/g, '\n') // Convert remaining \r to \n
            .replace(/\u00A0/g, ' ') // Replace non-breaking spaces with regular spaces
            .replace(/\u2013/g, '-') // Replace en-dash with regular dash
            .replace(/\u2014/g, '-') // Replace em-dash with regular dash
            .replace(/\u2018/g, "'") // Replace left single quote
            .replace(/\u2019/g, "'") // Replace right single quote
            .replace(/\u201C/g, '"') // Replace left double quote
            .replace(/\u201D/g, '"') // Replace right double quote
            .replace(/\u2026/g, '...'); // Replace ellipsis

          // Check text size (approximately)
          const textSizeBytes = new Blob([processedText]).size;
          // Warning threshold: 200 KB
          const WARN_BYTES = 200 * 1024;
          if (textSizeBytes > WARN_BYTES) {
            const kb = Math.round(textSizeBytes / 1024);
            const proceed =
              typeof window !== 'undefined'
                ? window.confirm(
                    `The text is quite long (~${kb} KB). Processing may time out.\n` +
                      'Consider splitting into smaller sections or uploading a PDF.\n' +
                      'Do you want to continue anyway?',
                  )
                : true;
            if (!proceed) {
              throw new Error(
                'Submission cancelled. Please split the text into smaller sections and try again.',
              );
            }
          }

          if (textSizeBytes > 5 * 1024 * 1024) {
            // 5MB hard limit
            throw new Error(
              'Text is too large to process. Please reduce the size or split into smaller sections.',
            );
          }

          console.log('Text preprocessing complete:', {
            originalLength: text.length,
            processedLength: processedText.length,
            sizeBytes: textSizeBytes,
          });
        }

        // Build request payload: use JSON when sending text-only to avoid multipart parsing overhead
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

        try {
          const isJsonPayload = !file && !!processedText;

          const requestInit: RequestInit = isJsonPayload
            ? {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  text: processedText,
                  currentAdditionalInfo,
                }),
                signal: controller.signal,
              }
            : ({
                method: 'POST',
                body: (() => {
                  const fd = new FormData();
                  if (file) fd.append('file', file);
                  if (processedText) fd.append('text', processedText);
                  if (currentAdditionalInfo)
                    fd.append('currentAdditionalInfo', currentAdditionalInfo);
                  return fd;
                })(),
                signal: controller.signal,
              } as RequestInit);

          const response = await fetch('/api/parse-medical-doc/initiate', requestInit);

          clearTimeout(timeoutId);

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            // Handle specific HTTP status codes
            if (response.status === 413) {
              throw new Error(
                'The text is too large to process. Please try splitting it into smaller sections or reduce the content size.',
              );
            } else if (response.status === 400) {
              throw new Error(
                errorData.error || 'Invalid input. Please check your text and try again.',
              );
            } else if (response.status === 500) {
              throw new Error('Server error occurred. Please try again in a few moments.');
            } else if (response.status === 504) {
              throw new Error(
                'Request timed out on the server. Please try again in a few moments or/and with simpler text.',
              );
            } else {
              throw new Error(errorData.error || `Request failed with status ${response.status}`);
            }
          }

          const data = await response.json().catch(() => {
            throw new Error(
              'Server response was not valid JSON. This might indicate a server timeout or error.',
            );
          });

          // Set job details
          setCurrentJobId(data.jobId);
          setJobStatus(data.status || 'PROCESSING');

          // Handle failed status from API response
          if (data.status === 'FAILED') {
            setError(data.error || 'Processing failed. Please try again.');
            return data.jobId;
          }

          // Manually set upload stage to complete (only if the stage exists)
          if (initialStages.UPLOAD) {
            handleWebSocketMessage({
              type: 'status',
              jobId: data.jobId,
              data: {
                id: 'UPLOAD',
                status: 'COMPLETE',
              },
            });
          }

          // Show parser UI
          setUiState('FULL');
          setSourceRoute(window.location.pathname);

          // Connect to WebSocket using the endpoint provided by the API
          if (data.websocketEndpoint) {
            connectToWebSocketWithEndpoint(data.jobId, data.websocketEndpoint);
          } else {
            connectToWebSocket(data.jobId);
          }

          return data.jobId;
        } catch (fetchError: any) {
          clearTimeout(timeoutId);

          if (fetchError.name === 'AbortError') {
            throw new Error(
              'Request timed out. The text might be too large or the server is busy. Please try again with smaller text.',
            );
          }

          // Re-throw other errors
          throw fetchError;
        }
      } catch (error: any) {
        setError(error.message || 'Failed to initiate processing');
        setJobStatus('FAILED');
        throw error;
      }
    },
    [connectToWebSocket, connectToWebSocketWithEndpoint, handleWebSocketMessage, sourceType],
  );

  // Retry processing
  const retryProcessing = useCallback(async (): Promise<boolean> => {
    if (!currentJobId) {
      setError('No job to retry');
      return false;
    }

    try {
      setError(null);
      setActiveTab('UPLOAD');

      const initialStages = getInitialStages(sourceType);
      setStages(initialStages);

      // Request status update from server
      const response = await fetch(`/api/parse-medical-doc/status?jobId=${currentJobId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to retry processing');
      }

      const data = await response.json();

      setJobStatus(data.status);

      // If job status is FAILED, set error
      if (data.status === 'FAILED') {
        setError(data.error || 'Processing failed. Please try again.');
        return false;
      }

      // Merge stages returned from the server with our initial stage map
      if (Array.isArray(data.stages)) {
        const baseStages = getInitialStages(sourceType);
        const fallbackNames = {
          ...getInitialStages('upload'),
          ...getInitialStages('text'),
        } as Stages;
        const updatedStages: Stages = { ...baseStages };

        (data.stages as StageUpdate[]).forEach((stage: StageUpdate) => {
          if (!stage?.id) return;
          const existing = updatedStages[stage.id as keyof Stages];
          const ensured =
            existing ||
            ({
              id: stage.id,
              name: (fallbackNames[stage.id]?.name as string) || stage.id,
              status: 'PENDING',
            } as any);

          updatedStages[stage.id as keyof Stages] = {
            ...ensured,
            status: stage.status,
            message: stage.message,
          } as any;
        });

        // If we’re in upload flow and job already progressed, ensure UPLOAD shows as COMPLETE
        if (
          sourceType === 'upload' &&
          updatedStages.UPLOAD &&
          updatedStages.UPLOAD.status !== 'COMPLETE' &&
          (data.status === 'PROCESSING' || data.status === 'COMPLETE' || data.status === 'FAILED')
        ) {
          updatedStages.UPLOAD = {
            ...updatedStages.UPLOAD,
            status: 'COMPLETE',
          } as any;
        }

        setStages(updatedStages);
      }

      if (data.result) {
        setExtractedData(data.result);
        if (data.status === 'COMPLETE') {
          setActiveTab('REVIEW');
        }
      }

      // Reconnect to WebSocket if job is still processing
      if (data.status === 'PENDING' || data.status === 'PROCESSING') {
        if (data.websocketEndpoint) {
          connectToWebSocketWithEndpoint(currentJobId, data.websocketEndpoint);
        } else {
          connectToWebSocket(currentJobId);
        }
      }

      return true;
    } catch (error: any) {
      setError(error.message || 'Failed to retry processing');
      setJobStatus('FAILED');
      return false;
    }
  }, [currentJobId, connectToWebSocket, connectToWebSocketWithEndpoint, sourceType]);

  // UI state functions
  const minimizeParser = useCallback(() => {
    setUiState('MINIMIZED');
  }, []);

  const maximizeParser = useCallback(() => {
    setUiState('FULL');
  }, []);

  const closeParser = useCallback(() => {
    // Only completely hide if not actively processing and no extracted data
    if (!isProcessing && !extractedData) {
      setUiState('HIDDEN');
      resetConnection();
    } else {
      // Otherwise minimize instead of hiding
      setUiState('MINIMIZED');
    }
  }, [isProcessing, extractedData, resetConnection]);

  // When component unmounts, disconnect WebSocket
  useEffect(() => {
    return () => {
      if (wsClientRef.current) {
        wsClientRef.current.disconnect();
      }
    };
  }, []);

  // Reconnect WebSocket on page navigation if we have an active job
  useEffect(() => {
    if (
      hydrated &&
      currentJobId &&
      jobStatus &&
      (jobStatus === 'PENDING' || jobStatus === 'PROCESSING') &&
      !wsClientRef.current
    ) {
      // Only connect if we don't already have a connection
      connectToWebSocket(currentJobId);
    }
  }, [hydrated, currentJobId, jobStatus, connectToWebSocket]);

  // Try to fetch job status when recovering from localStorage
  useEffect(() => {
    // Only execute after hydration is complete
    if (hydrated && currentJobId && !jobStatus) {
      retryProcessing().catch(err => {
        console.error('Failed to recover job status from localStorage:', err);
        setCurrentJobId(null);
      });
    }
  }, [hydrated, currentJobId, jobStatus, retryProcessing]);

  // Build context value
  const contextValue: DocumentParserContextState = {
    currentJobId,
    jobStatus,
    stages,
    error,
    uiState,
    isProcessing,
    activeTab,
    initiateProcessing,
    minimizeParser,
    maximizeParser,
    closeParser,
    retryProcessing,
    setActiveTab,
    extractedData,
    setExtractedData,
    clearJob,
    uploadedFile,
    setUploadedFile,
    sourceRoute,
    sourceType,
    sourceText,
    setSourceType,
    setSourceText,
  };

  return (
    <DocumentParserContext.Provider value={contextValue}>{children}</DocumentParserContext.Provider>
  );
};

// Hook to use the context
export function useDocumentParser(): DocumentParserContextState {
  const context = useContext(DocumentParserContext);

  if (!context) {
    throw new Error('useDocumentParser must be used within a DocumentParserProvider');
  }

  return context;
}
